/**
 * Converts a number in bytes to a human-readable string.
 *
 * Use an array dictionary of units to be accessed based on the exponent. Use Number.toPrecision() to truncate the number to a certain number of digits.
 * Return the prettified string by building it up, taking into account the supplied options and whether it is negative or not. Omit the second argument,
 * precision, to use a default precision of 3 digits. Omit the third argument, addSpace, to add space between the number and unit by default.
 *
 * @example prettyBytes(1000); // "1 KB"
 * @example prettyBytes(-27145424323.5821, 5); // "-27.145 GB"
 * @example prettyBytes(123456789, 3, false); // "123MB"
 *
 * @param num
 * @param precision
 * @param addSpace
 * @returns {string}
 */
import i18n from '@/lang/lang'

/**
 * Use toLocaleString() to convert a float-point arithmetic to the Decimal mark form. It makes a comma separated string from a number.
 *
 * @example toDecimalMark(12305030388.9087); // "12,305,030,388.909"
 *
 * @param num
 * @param locale
 * @param decimals
 * @returns {string}
 */
export const toDecimalMark = (num, decimals = 2, locale = i18n.code) => {
  return num ? num.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }) : ''
}

export const toDecimalSignificant = (num, decimals = 3, locale = i18n.code) => {
  return num ? num.toLocaleString(locale, {
    maximumSignificantDigits: decimals
  }) : ''
}

/**
 * Use toLocaleString() to convert a float-point arithmetic to the Decimal mark form. It makes a comma separated string from a number.
 *
 * @example toDecimalMarkRange(12305030388.9087); // "12,305,030,388.909"
 *
 * @param num
 * @param locale
 * @param minDecimals
 * @param maxDecimals
 * @returns {string}
 */
export const toDecimalMarkRange = (num, minDecimals = 0, maxDecimals = 2, locale = i18n.code) => {
  return num ? num.toLocaleString(locale, {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals
  }) : ''
}

export const toNumberFormatNotationShort = (num, lower = true, locale = i18n.code, useMillions = false) => {
  if (!num) return ''

  let o
  if (useMillions) {
    // For millions mode, manually format to avoid billions notation
    if (Math.abs(num) >= 1000000000) {
      // Convert billions to millions and format
      const millions = num / 1000000
      // Truncate to 3 significant digits without rounding
      const truncated = Math.floor(millions * 1000) / 1000
      o = truncated.toLocaleString(locale, {
        maximumFractionDigits: 3
      }) + 'm'
    } else if (Math.abs(num) >= 1000000) {
      // Already in millions range
      const millions = num / 1000000
      // Truncate to 3 significant digits without rounding
      const truncated = Math.floor(millions * 1000) / 1000
      o = truncated.toLocaleString(locale, {
        maximumFractionDigits: 3
      }) + 'm'
    } else if (Math.abs(num) >= 1000) {
      // Thousands
      const thousands = num / 1000
      // Truncate to 3 significant digits without rounding
      const truncated = Math.floor(thousands * 1000) / 1000
      o = truncated.toLocaleString(locale, {
        maximumFractionDigits: 3
      }) + 'k'
    } else {
      // Less than 1000, format normally
      o = num.toLocaleString(locale, {
        maximumSignificantDigits: 3
      })
    }
  } else {
    // Original behavior with compact notation
    o = num.toLocaleString(locale, {
      notation                : 'compact',
      compactDisplay          : 'short',
      maximumSignificantDigits: 3
    })
  }

  return lower ? o.toLocaleLowerCase(locale) : o
}

/**
 * Formats a date to a short format with month and year
 *
 * @example formatDateShort(new Date('2020-09-17')); // "Sep 20"
 * @example formatDateShort(new Date('2020-09-17'), true); // "17 Sep 20"
 *
 * @param date - Date object or date string
 * @param includeDay - Whether to include the day in the format
 * @param sliceYear - Whether to slice the year to 2 digits
 * @returns {string}
 */
export const formatDateShort = (date, includeDay = false, sliceYear = 2) => {
  if (!date) return ''

  const d = new Date(date)
  if (isNaN(d.getTime())) return ''

  const month = d.toLocaleDateString('en-US', { month: 'short' })
  const year = d.getFullYear().toString().slice(-sliceYear)

  if (includeDay) {
    const day = d.getDate()
    return `${day} ${month} ${year}`
  }

  return `${month} ${year}`
}

/**
 * Returns cloned object
 *
 * @example clone([1, 2, 3]); // [1, 2, 3]
 *
 * @param obj
 * @returns {any}
 */
export const clone = obj => JSON.parse(JSON.stringify(obj))

/**
 * Adjusts a number to the specified digit.
 *
 * @param {"round" | "floor" | "ceil"} type The type of adjustment.
 * @param {number} value The number.
 * @param {number} exp The exponent (the 10 logarithm of the adjustment base).
 * @returns {number} The adjusted value.
 */
function decimalAdjust (type, value, exp) {
  type = String(type)
  if (!['round', 'floor', 'ceil'].includes(type)) {
    throw new TypeError(
      "The type of decimal adjustment must be one of 'round', 'floor', or 'ceil'."
    )
  }
  exp = Number(exp)
  value = Number(value)
  if (exp % 1 !== 0 || Number.isNaN(value)) {
    return NaN
  } else if (exp === 0) {
    return Math[type](value)
  }
  const [magnitude, exponent = 0] = value.toString().split('e')
  const adjustedValue = Math[type](`${magnitude}e${exponent - exp}`)
  // Shift back
  const [newMagnitude, newExponent = 0] = adjustedValue.toString().split('e')
  return Number(`${newMagnitude}e${Number(newExponent) + exp}`)
}

export const floor10 = (value, exp) => decimalAdjust('floor', value, exp)
export const ceil10 = (value, exp) => decimalAdjust('ceil', value, exp)
export const round10 = (value, exp) => decimalAdjust('round', value, exp)

/**
 * Computes the bounds for a Y-axis based on the values provided.
 */
export const computeYAxisBounds = (values, steps = 5) => {
  const arr = Array.isArray(values) ? values.filter(v => Number.isFinite(v)) : []
  if (!arr.length) return { min: 0, max: 0, interval: 1, steps }
  const minV = Math.min(...arr)
  const maxV = Math.max(...arr)
  const range = (maxV - minV)
  const rawStep = range / steps
  const exp = Math.floor(Math.log10(rawStep))
  const interval = ceil10(rawStep, exp)
  const pct = 0.0009
  const min = floor10(minV * (1 - pct) / interval, 0) * interval
  const max = ceil10(maxV * (1 + pct) / interval, 0) * interval
  return { min, max, interval }
}

export const computeXAxisBounds = (values, steps = 10) => {
  const arr = Array.isArray(values) ? values.filter(v => Number.isFinite(v)) : []
  if (!arr.length) return { min: 0, max: 0, interval: 1, steps }
  const minV = Math.min(...arr)
  const maxV = Math.max(...arr)
  const min = floor10(minV, 0)
  const max = ceil10(maxV, 0)
  return { min, max }
}
